import { useParams } from "react-router";
import { ProductData, ProductShoppingCart } from "../Cart/cart.dto";
import Navbar from "../Navbar/Navbar";
import ProductCard from "../ProductCard/ProductCard";
import { fetchProduct } from "@/api/ArticlesAPI";
import { useQuery } from "@tanstack/react-query";
import LoadingElement from "../Loading/LoadingElement";
import { toast } from "../ui/use-toast";
import { useCart } from "../Cart/CartContext";
import React from "react";

function ProductDetail() {
  const queryParams = useParams();
  const productId = queryParams.productId;
  const { addToCart } = useCart();

  const { data, isLoading, error } = useQuery({
    queryKey: ["product", productId],
    queryFn: fetchProduct,
  });

  if (isLoading) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement /></p>;
  if (productId === undefined) return <p>A aparut o eroare pentru extragerea informatiilor acestui produs.</p>;

  if (error) {
    toast({ description: "Exista o eroare la preluarea informatiilor despre acest produs." });
    return <></>;
  }
  
  const productData = data as ProductData;

  async function handleAddToCart() {
    const newCartItem: ProductShoppingCart  = {
      ...productData,
      articleId: productData.id,
      quantity: productData.quantity,
      salesPrice: productData.salesPriceValue,
      salesUnit: productData.salesUnit,
    };

    try {
      await addToCart(newCartItem, productData.isTransit);
      toast({ description: "Produs adaugat in cos cu success" });
    } catch (error) {
      console.error("Failed to add item to cart:", error);
      toast({ description: "A aparut o eroare. Va rugam incercati din nou." });
    }
  }

  return (
    <>
      <Navbar />
      {/* Desktop Version */}
      <div className="hidden md:block">
        <div className="flex justify-center space-x-3 max-w-screen-xl">
          <div className="w-1/2 px-24 ml-40 mt-4 text-center">
            <ProductCard productData={productData} autoPlayCarousel={true} />
          </div>
          <div className="w-1/2 px-24 mt-4">
            <ProductDescription productData={productData} handleAddToCart={handleAddToCart} />
          </div>
        </div>
      </div>
      {/* Mobile Version */}
      <div className="mx-auto max-w-screen-xl md:hidden -z-10">
        <div className="flex flex-col flex-wrap justify-center lg:flex-nowrap md:pt-6 lg:pt-6">
          <div className="text-center lg:px-12">
            <ProductCard productData={productData} autoPlayCarousel={true}/>
          </div>
          <div className="p-4 lg:px-12 max-w-lg">
            <ProductDescription productData={productData} handleAddToCart={handleAddToCart} />
          </div>
        </div>
      </div>

    </>
  );
}

function ProductDescription({ productData, handleAddToCart }: { productData: ProductData; handleAddToCart: () => void }) {
  return (
      <div className="flow-root">
        <dl className="-my-3 divide-y divide-gray-100 text-sm">
          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium text-gray-900">Tip produs</dt>
            <dd className="text-gray-700 sm:col-span-2">{productData.hierarchyLocal.categoryLabel}</dd>
          </div>

          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium text-gray-900">Categorie produs</dt>
            <dd className="text-gray-700 sm:col-span-2">{productData.hierarchyLocal.marketLabel}</dd>
          </div>

          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium text-gray-900">Unitatea de masura</dt>
            <dd className="text-gray-700 sm:col-span-2">
              {productData.salesUnit === 'A' && <p>Bucata</p>} {productData.salesUnit === 'K' &&
                <p>Kilogram</p>} {productData.salesUnit === 'M' && <p>Metru</p>}
            </dd>
          </div>

          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium text-gray-900">Unitate de vanzare</dt>
            <dd className="text-gray-700 sm:col-span-2">
              {productData.multiplier}
            </dd>
          </div>

          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium text-gray-900">Taxe SGR</dt>
            <dd className="text-gray-700 sm:col-span-2">{productData.taxArticles[0] && productData.taxArticles[0].tax?.activeTaxPrice ? productData.taxArticles[0].tax.activeTaxPrice?.taxPriceValue + ' RON' : '-'}</dd>
          </div>

          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium text-gray-900">
              <div className="whitespace-pre-line">{`Specificatii
            (W x H x L)`}</div>
            </dt>
            <dd className="text-gray-700 sm:col-span-2">
              <div>
                {`${productData.width} x ${productData.height} x ${productData.length}`}
              </div>
            </dd>
          </div>

          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-3 sm:gap-4">
            <dt className="font-medium text-gray-900">
              <div>Greutate</div>
            </dt>

            <dd className="text-gray-700 sm:col-span-2">
              <div>{`${productData.weight} ${productData.measureUnit}`}</div>
            </dd>
          </div>
        </dl>

        <div className="mt-12"></div>
        {process.env.REACT_APP_SHOW_MAINTENANCE_NOTIFICATION === 'true' && <div className="fixed inset-x-0 bottom-0 p-4 z-20">
          <div className="rounded-lg bg-red-600 px-4 py-3 text-white shadow-lg">
            <p className="text-center text-sm font-medium">
              {process.env.REACT_APP_MAINTENANCE_MESSAGE}
            </p>
          </div>
        </div>}
      </div>
  );
}

export default ProductDetail;
